/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import React, { FunctionComponent, useCallback, useEffect, useState } from 'react';

import { ScreenType } from 'client-code/previews';
import PropTypes from 'prop-types';

import { NdvrInteractor } from '../../../core/interactors';
import { Locale, LocaleKeys } from '../../../core/locale';
import { Log } from '../../../core/logging';
import { SharedPrefConstants, SharedPreferences } from '../../../core/storage';
import { RootState, useTivoDispatch, useTivoSelector } from '../../../core/tivo/tivo-redux-wrapper';
import { UIOrchestrator, UIOrchestratorState } from '../../../core/uiorchestrator';
import { ListTypes } from '../../../core/utils';
import { CollectionActions } from '../../../store/collectionscreen/CollectionActions';
import { CollectionFilters } from '../../../store/collectionscreen/CollectionFilters';
import { setCollectionPageLoading } from '../../../store/collectionscreen/collectionscreen.actions';
import { getUnifiedCollectionId } from '../../../store/collectionscreen/collectionscreen.reducers';
import { TAG } from '../../../store/videoplayerui/onelineguide/onelineguide.constants';
import { List, ListLabelItem, Mousetrap, RightPreview } from '../../common';
import { CollectionScreenOnePassItemsProps } from './CollectionScreenOnePassItemsProps';

const CollectionScreenOnePassItems: FunctionComponent<CollectionScreenOnePassItemsProps> = ({
    onePassItems,
    selectedFilter,
}): JSX.Element => {
    const dispatch = useTivoDispatch();
    const [onePassPreviewItem, setOnePassPreviewItem] = useState<any>({});
    const [refreshPreview, setRefreshPreview] = useState<boolean>(false);
    const collectionOnePassItems = useTivoSelector(
        (state: RootState) => state.list[ListTypes.COLLECTION_ONEPASS_ITEMS],
    );
    const { isFocused = false, position = 0 } = collectionOnePassItems || {};

    const renderOnePassItem = (item: any, itemClass: string, index: number) => {
        return (
            <Mousetrap
                key={`onePass-item-${index}`}
                value={`${index}_${ListTypes.COLLECTION_ONEPASS_ITEMS}`}
                className={`item ${itemClass}`}
            >
                <ListLabelItem
                    label={item}
                    customContainerStyle="cs-item-onepass-container"
                    customLabelStyle="cs-item-onepass-title"
                />
            </Mousetrap>
        );
    };

    const getHighlightedItem = useCallback((): string => {
        const sharedPreferences = new SharedPreferences(
            SharedPrefConstants.COLLECTION_ONE_PASS_ACTION_PREF_KEY,
        );
        const onePassActionList = sharedPreferences.getJson(
            SharedPrefConstants.COLLECTION_ONE_PASS_ACTION_LIST,
        ) as any;

        const action = onePassActionList?.[position]?.kernel?.actionId || '';
        if (isFocused)
            switch (action) {
                case CollectionActions.CANCEL_ONEPASS_V2:
                    return CollectionActions.CANCEL_ONEPASS_V2;
                case CollectionActions.MODIFY_ONEPASS_V2:
                    return CollectionActions.MODIFY_ONEPASS_V2;
                default:
                    return '';
            }
        return '';
    }, [isFocused, position]);

    const fetchOnePassPreviewItem = () => {
        try {
            const ndvrInteractor = new NdvrInteractor();
            ndvrInteractor
                .getOnePassPreviewItemByUCId({
                    screenType: ScreenType.SeriesScreenItem,
                    unifiedCollectionId: getUnifiedCollectionId(),
                    includeChannel: 'true',
                })
                .then((response) => {
                    setOnePassPreviewItem(response.preview);
                });
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : JSON.stringify(error);
            Log.e(TAG, errorMessage);
            setOnePassPreviewItem({});
        }
    };

    const handleCollectionScreenOnePassRefresh = (state: UIOrchestratorState) => {
        if (state === UIOrchestratorState.ONE_PASS_MODIFIED) {
            setRefreshPreview((prev) => !prev);
        }
    };

    useEffect(() => {
        dispatch(setCollectionPageLoading(false));
    }, [selectedFilter]);

    useEffect(() => {
        if (isFocused && selectedFilter === CollectionFilters.ONEPASS_OPTIONS_DETAIL) {
            fetchOnePassPreviewItem();
        }
    }, [isFocused, refreshPreview]);

    useEffect(() => {
        UIOrchestrator.stateChangeListener.subscribe(handleCollectionScreenOnePassRefresh);

        return () => {
            UIOrchestrator.stateChangeListener.cancel(handleCollectionScreenOnePassRefresh);
        };
    }, []);

    return (
        <>
            <div className="cs-onepass-items-layout">
                <List
                    type={ListTypes.COLLECTION_ONEPASS_ITEMS}
                    itemList={onePassItems}
                    generateItemView={(item, itemClass, index) =>
                        renderOnePassItem(item, itemClass, index || 0)
                    }
                />
                {selectedFilter === CollectionFilters.GET_THIS_SHOW_DETAILS && (
                    <div className="item-bottom-text-container">
                        <p className="item-bottom-text">
                            {Locale.getString(LocaleKeys.CREATE_ONEPASS_BOTTOM_TEXT)}
                        </p>
                    </div>
                )}
            </div>
            <RightPreview highlightedOption={getHighlightedItem()} preview={onePassPreviewItem} />
        </>
    );
};

CollectionScreenOnePassItems.propTypes = {
    onePassItems: PropTypes.array.isRequired,
    selectedFilter: PropTypes.string.isRequired,
};

export default CollectionScreenOnePassItems;
